/**
 * TanStack Query hooks for custom evaluator sets.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import customEvalsService, {
  type CreateCustomEvalSetRequest,
  type CustomEvalSetListResponse,
  type CustomEvalSetResponse,
  type CustomEvalSetVersionCreate,
  type CustomEvalSetVersionResponse,
  type CustomEvalSetVersionUpdateRequest,
  type EvaluatorBuilderRequest,
  type EvaluatorBuilderResponse,
} from '@/lib/api/customEvals';
import { queryKeys } from '@/queries/keys';

export function useCustomEvalSets(
  orgId: string | undefined,
  options?: { includeVersions?: boolean },
) {
  return useQuery<CustomEvalSetListResponse>({
    queryKey: queryKeys.customEvals.sets(orgId ?? ''),
    queryFn: async () => {
      const response = await customEvalsService.listEvalSets(
        orgId!,
        options?.includeVersions ?? false,
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load custom evaluators');
      }
      return response.data;
    },
    enabled: !!orgId,
  });
}

export function useCustomEvalSet(
  orgId: string | undefined,
  setId: string | null | undefined,
  options?: { enabled?: boolean; includeVersions?: boolean },
) {
  return useQuery<CustomEvalSetResponse>({
    queryKey: queryKeys.customEvals.set(orgId ?? '', setId ?? ''),
    queryFn: async () => {
      const response = await customEvalsService.getEvalSet(
        setId!,
        orgId!,
        options?.includeVersions ?? true,
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load custom evaluator');
      }
      return response.data;
    },
    enabled: !!orgId && !!setId && (options?.enabled ?? true),
  });
}

export function useGenerateCriteriaPreview() {
  return useMutation<EvaluatorBuilderResponse, Error, EvaluatorBuilderRequest>({
    mutationFn: async (data) => {
      const response = await customEvalsService.generateCriteriaPreview(data);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to generate rubric preview');
      }
      return response.data;
    },
  });
}

export function useCreateCustomEvalSet() {
  const queryClient = useQueryClient();
  return useMutation<CustomEvalSetResponse, Error, CreateCustomEvalSetRequest>({
    mutationFn: async (data) => {
      const response = await customEvalsService.createEvalSet(data);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create custom evaluator');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customEvals.sets(variables.organization_id),
      });
      queryClient.invalidateQueries({
        queryKey: ['performance-overview', variables.organization_id],
      });
    },
  });
}

interface PublishVersionVariables {
  setId: string;
  versionId: string;
  organizationId: string;
  userId?: string;
}

export function usePublishCustomEvalVersion() {
  const queryClient = useQueryClient();
  return useMutation<CustomEvalSetResponse, Error, PublishVersionVariables>({
    mutationFn: async ({ setId, versionId, organizationId, userId }) => {
      const response = await customEvalsService.publishVersion(setId, versionId, {
        organization_id: organizationId,
        user_id: userId,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to publish custom evaluator');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customEvals.sets(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customEvals.set(variables.organizationId, variables.setId),
      });
      queryClient.invalidateQueries({
        queryKey: ['performance-overview', variables.organizationId],
      });
    },
  });
}

interface CreateVersionVariables {
  setId: string;
  organizationId: string;
  data: CustomEvalSetVersionCreate;
}

export function useCreateCustomEvalVersion() {
  const queryClient = useQueryClient();
  return useMutation<CustomEvalSetVersionResponse, Error, CreateVersionVariables>({
    mutationFn: async ({ setId, organizationId, data }) => {
      const response = await customEvalsService.createVersion(setId, organizationId, data);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create draft version');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customEvals.sets(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customEvals.set(variables.organizationId, variables.setId),
      });
      queryClient.invalidateQueries({
        queryKey: ['performance-overview', variables.organizationId],
      });
    },
  });
}

interface UpdateVersionVariables {
  setId: string;
  versionId: string;
  organizationId: string;
  data: CustomEvalSetVersionUpdateRequest;
}

export function useUpdateCustomEvalVersion() {
  const queryClient = useQueryClient();
  return useMutation<CustomEvalSetVersionResponse, Error, UpdateVersionVariables>({
    mutationFn: async ({ setId, versionId, organizationId, data }) => {
      const response = await customEvalsService.updateVersion(
        setId,
        versionId,
        organizationId,
        data,
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update draft version');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customEvals.sets(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customEvals.set(variables.organizationId, variables.setId),
      });
      queryClient.invalidateQueries({
        queryKey: ['performance-overview', variables.organizationId],
      });
    },
  });
}

interface DeleteVersionVariables {
  setId: string;
  versionId: string;
  organizationId: string;
}

export function useDeleteCustomEvalVersion() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeleteVersionVariables>({
    mutationFn: async ({ setId, versionId, organizationId }) => {
      const response = await customEvalsService.deleteVersion(
        setId,
        versionId,
        organizationId,
      );
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete draft version');
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customEvals.sets(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.customEvals.set(variables.organizationId, variables.setId),
      });
      queryClient.invalidateQueries({
        queryKey: ['performance-overview', variables.organizationId],
      });
    },
  });
}

interface DeleteSetVariables {
  setId: string;
  organizationId: string;
}

export function useDeleteCustomEvalSet() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeleteSetVariables>({
    mutationFn: async ({ setId, organizationId }) => {
      const response = await customEvalsService.deleteEvalSet(setId, organizationId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete evaluator');
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.customEvals.sets(variables.organizationId),
      });
      queryClient.removeQueries({
        queryKey: queryKeys.customEvals.set(variables.organizationId, variables.setId),
      });
      queryClient.invalidateQueries({
        queryKey: ['performance-overview', variables.organizationId],
      });
    },
  });
}
